import { ButtonWithLoading, ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Checkbox, FormControlLabel, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Grid2";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/lab/Alert";
import { type SyntheticEvent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { Form } from "../../../models/Form";
import { useFormsContext } from "../../../context/FormsContext";
import { INITIAL_VALUES_FORM } from "../../../models/FormInitialValues";
import { upsertForm } from "../../../repositories/FormsRepository";

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	data: Form | null;
	newMode?: boolean;
};

export const NewAndEditFormModal = ({
	show,
	onHide,
	data,
	newMode = false,
}: Props) => {
	const {
		state: { formSelectedInTable, FormId },
		actions: {
			setFormNameFormSelectedInTable,
			setIsEnabledFormSelectedInTable,
			setIncludesCommentFormSelectedInTable,
			setFormId,
		},
	} = useFormsContext();

	const [editedSuccess, setEditedSuccess] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);

	const [error, setError] = useState<string>("");
	const [deleteByIdError, setDeleteByIdError] = useState<string>("");
	const [errorAssetPath, setErrorAssetPath] = useState<string>("");

	const {
		control,
		handleSubmit,
		watch,
		setValue,
		formState: { errors },
		reset,
	} = useForm<Form>({ defaultValues: INITIAL_VALUES_FORM });

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setEditedSuccess(false);
	};
	const handleCancel = async () => {
		onHide(false);
	};

	const formName = watch("FormName");

	const changeFormName = () => {
		setFormNameFormSelectedInTable(formName);
	};

	const isEnabled = watch("IsEnabled");

	const changeIsEnabled = () => {
		setIsEnabledFormSelectedInTable(isEnabled);
	};
	const includesComment = watch("IncludesComment");

	const changeIncludesComment = () => {
		setIncludesCommentFormSelectedInTable(includesComment);
	};

	const onSubmit = async (formData: Form) => {
		setIsSubmitLoading(true);
		const res = await upsertForm(
			FormId,
			formData?.FormName,
			formData?.IsEnabled,
			formData?.LayoutCode,
			formData?.IncludesComment,
		);
		if (res.ok) {
			reset(INITIAL_VALUES_FORM);
			setIsSubmitLoading(false);
			setEditedSuccess(true);
			setFormId(null);
			onHide(true);
		} else {
			setIsSubmitLoading(false);
			setError(res.message);
		}
	};

	useEffect(() => {
		if (data !== null && show && !newMode) {
			setFormNameFormSelectedInTable(data.FormName);
			setIsEnabledFormSelectedInTable(data.IsEnabled);
			setIncludesCommentFormSelectedInTable(data.IncludesComment);
			setValue("FormName", data.FormName);
			setValue("IsEnabled", data.IsEnabled);
			setValue("IncludesComment", data.IncludesComment);
			setIsSubmitLoading(false);
		} else {
			setFormId(null);
			reset(INITIAL_VALUES_FORM);
		}
	}, [show]);

	useEffect(() => {
		changeFormName();
	}, [formName]);

	useEffect(() => {
		changeIsEnabled();
	}, [isEnabled]);

	useEffect(() => {
		changeIncludesComment();
	}, [includesComment]);

	return (
		<>
			<MesfModal
				title={newMode ? "NEW FORM" : "EDIT FORM"}
				open={show}
				handleClose={() => handleCancel()}
				maxWidth="sm"
				id="form-modal"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers>
						<Grid2 container spacing={2}>
							<Grid2 size={12}>
								<Controller
									name="FormName"
									control={control}
									rules={{
										validate: (value) => value !== "",
									}}
									render={({ field: { onChange, value } }) => (
										<TextField
											label="Form Name"
											slotProps={{ inputLabel: { shrink: true } }}
											variant="outlined"
											fullWidth
											error={!!errors.FormName}
											margin="dense"
											autoComplete="off"
											value={value}
											onChange={(e) => onChange(e)}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={6}>
								<Controller
									name="IsEnabled"
									control={control}
									defaultValue={formSelectedInTable?.IsEnabled}
									render={({ field }) => (
										<FormControlLabel
											sx={{ userSelect: "none" }}
											control={
												<Checkbox
													checked={field.value as boolean}
													onChange={(e) => field.onChange(e.target.checked)}
													name="IsEnabled"
													color="primary"
												/>
											}
											label="Is Enabled"
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={6}>
								<Controller
									name="IncludesComment"
									control={control}
									render={({ field }) => (
										<FormControlLabel
											sx={{ userSelect: "none" }}
											control={
												<Checkbox
													checked={field.value as boolean}
													onChange={(e) => field.onChange(e.target.checked)}
													name="IncludesComment"
													color="primary"
												/>
											}
											label="Includes Comment"
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 16px 16px" }}>
						<Grid2 container spacing={2} justifyContent="flex-end" width="100%">
							<Grid2 size={{ md: 3, xs: 12 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={handleCancel}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }}>
								<ButtonWithLoading
									fullWidth
									isLoading={isSubmitLoading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</ButtonWithLoading>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<Snackbar
				open={editedSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="info" onClose={handleClose}>
					Success
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal
				error={deleteByIdError}
				onHide={() => setDeleteByIdError("")}
			/>
			<ErrorModal error={errorAssetPath} onHide={() => setErrorAssetPath("")} />
		</>
	);
};
